export const DEFAULT_INVESTMENT_TYPES = [
    'Growth Investment',
    'Minority Investment',
    'Majority Acquisition',
    'Full Acquisition',
    'Merger',
    'Carve-Out',
    'Buy-and-Build / Roll-Up',
    'Internal Transformation',
    'Not Applicable',
];

export const DEFAULT_CUSTOMER_STAGES = ['Startup', 'Scale-Up', 'Growth', 'Mature', 'Enterprise', 'Public Company', 'Other'];

export const DEFAULT_CUSTOMER_TYPES = ['B2B', 'B2C', 'B2B2C', 'Enterprise', 'SME', 'Public Sector'];

export const DEFAULT_SCOPE_AREAS = [
    'Applications & Systems',
    'Infrastructure & Cloud',
    'Networks & Connectivity',
    'Workspace Technology',
    'Software Engineering',
    'Product Management & Delivery',
    'Technology Strategy',
    'Enterprise Architecture',
    'Cybersecurity',
    'Data Privacy & Compliance',
    'IT Governance & Risk',
    'Business Continuity & Disaster Recovery',
    'Data & Analytics',
    'AI & Data Science',
    'IT Operations & Service Management',
    'Vendors & Third Parties',
];

export const DEFAULT_PROJECT_INVESTMENT_TYPE = 'Growth Investment';
export const DEFAULT_PROJECT_CUSTOMER_TYPE = 'B2B';
export const DEFAULT_PROJECT_CUSTOMER_STAGE = 'Growth';
export const DEFAULT_PROJECT_SCOPE_AREAS = ['Applications & Systems', 'Infrastructure & Cloud', 'Cybersecurity'];

function normalizeNamedOptions(options, fallbackNames) {
    if (Array.isArray(options) && options.length) {
        return options
            .filter(option => option && option.name)
            .map(option => ({
                name: option.name,
                value: option.name,
                text: option.name,
                label: option.name,
            }));
    }
    return fallbackNames.map(name => ({ name, value: name, text: name, label: name }));
}

export function resolveWorkspaceInvestmentTypeOptions(workspace, { includeValue } = {}) {
    const options = normalizeNamedOptions(workspace?.investment_type_options, DEFAULT_INVESTMENT_TYPES);
    if (includeValue && !options.some(o => o.value === includeValue)) {
        options.push({ name: includeValue, value: includeValue, text: includeValue, label: includeValue });
    }
    return options;
}

export function resolveWorkspaceCustomerTypeOptions(workspace, { includeValue } = {}) {
    const options = normalizeNamedOptions(workspace?.customer_type_options, DEFAULT_CUSTOMER_TYPES);
    if (includeValue && !options.some(o => o.value === includeValue)) {
        options.push({ name: includeValue, value: includeValue, text: includeValue, label: includeValue });
    }
    return options;
}

export function resolveWorkspaceCustomerStageOptions(workspace, { includeValue } = {}) {
    const options = normalizeNamedOptions(workspace?.customer_stage_options, DEFAULT_CUSTOMER_STAGES);
    if (includeValue && !options.some(o => o.value === includeValue)) {
        options.push({ name: includeValue, value: includeValue, text: includeValue, label: includeValue });
    }
    return options;
}

export function resolveWorkspaceScopeAreaOptions(workspace, { includeValues = [] } = {}) {
    const options = normalizeNamedOptions(workspace?.scope_area_options, DEFAULT_SCOPE_AREAS);
    const seen = new Set(options.map(o => o.value));
    includeValues.forEach(value => {
        const label = (value || '').trim();
        if (label && !seen.has(label)) {
            seen.add(label);
            options.push({ name: label, value: label, text: label, label });
        }
    });
    return options;
}

export function defaultProjectMetadataFormValues() {
    return {
        investment_type: '',
        customer_type: '',
        customer_stage: '',
        scope_areas: [],
    };
}

/** Step 1 — Add New Project (investment type only among deal metadata). */
export function newProjectCreateFormValues() {
    return {
        investment_type: DEFAULT_PROJECT_INVESTMENT_TYPE,
        customer_type: '',
        customer_stage: '',
        scope_areas: [],
    };
}

/** Add Project Details screen (before NDA). */
export function newProjectDetailsFormValues() {
    return {
        customer_type: '',
        customer_stage: '',
        scope_areas: [],
        engagement_objective: '',
    };
}

/** @deprecated use newProjectDetailsFormValues */
export function newProjectDealMetadataFormValues() {
    return newProjectDetailsFormValues();
}

export const PROJECT_SCOPE_SELECT_STYLES = {
    control: (provided, state) => ({
        ...provided,
        borderColor: 'var(--Neutral-Gray-5)',
        borderBottomColor: 'var(--Neutral-Gray-6)',
        minHeight: '32px',
        fontSize: 14,
        boxShadow: state.isFocused ? '0 0 0 1px var(--Brand-Secondary)' : 'none',
    }),
    valueContainer: provided => ({
        ...provided,
        height: 'auto',
        minHeight: '32px',
        padding: '2px 6px',
        flexWrap: 'wrap',
    }),
    input: provided => ({
        ...provided,
        margin: '0px',
    }),
    indicatorSeparator: () => ({
        display: 'none',
    }),
    indicatorsContainer: provided => ({
        ...provided,
        height: '32px',
    }),
    multiValue: provided => ({
        ...provided,
        backgroundColor: 'var(--Neutral-Gray-5)',
        color: 'var(--Neutral-Gray-8)',
        margin: '1px',
    }),
    multiValueLabel: provided => ({
        ...provided,
        color: 'var(--Neutral-Gray-8)',
        fontSize: '12px',
    }),
    multiValueRemove: provided => ({
        ...provided,
        color: 'var(--Neutral-Gray-8)',
        ':hover': {
            backgroundColor: 'var(--Neutral-Gray-6)',
            color: 'var(--Neutral-Gray-9)',
        },
    }),
    menu: provided => ({
        ...provided,
        zIndex: 9999,
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? 'var(--Neutral-Gray-5)' : state.isFocused ? 'var(--Neutral-Gray-3)' : 'white',
        color: state.isSelected ? 'var(--Neutral-Gray-9)' : 'var(--Neutral-Gray-8)',
        fontWeight: 400,
    }),
};

export const ENGAGEMENT_OBJECTIVE_PLACEHOLDER = `Example:
Assess scalability of technology platform
Evaluate integration complexity
Validate cybersecurity posture
Support acquisition decision
Identify post-merger priorities
Assess technology operating model`;
